import React, { useState } from 'react';
import { formatCurrency, calcItemEffectivePrice, PAYMENT_TYPES } from '../utils/checkoutCalculations';
import toast from '../../shared/Toast';

/**
 * LoyaltyScreen
 * Redeem a client's loyalty reward at the till. A reward is "one free eligible
 * item": the staff picks the reward and the single line it comps (taken to £0 at
 * its price), confirms the scheme's terms, and we push a paymentType-16 comp row.
 * No money moves here and no API call — the balance nets it off automatically,
 * the BE validates/enriches the row on checkout-edit and consumes the card on
 * completion. Mirrors VoucherScreen's flow + design language.
 */
const LoyaltyScreen = ({ state, actions, onBack, currencySymbol }) => {
  const fmt = (amount) => formatCurrency(amount, currencySymbol);

  // Only cards with something to redeem are offerable here. The till may pass
  // every card (so it can show progress on a disabled button), so filter.
  const cards = (state.loyaltyRewards || []).filter((c) => (c.rewardsAvailable || 0) > 0);
  const [schemeId, setSchemeId] = useState(cards.length === 1 ? String(cards[0].schemeId) : '');
  const [lineKey, setLineKey] = useState('');
  const [tnc, setTnc] = useState(false);
  const [error, setError] = useState('');

  const selectedCard = cards.find((c) => String(c.schemeId) === String(schemeId)) || null;
  const scope = selectedCard?.redeemScope || 'anything';
  const scopeNoun = scope === 'products' ? 'product' : scope === 'services' ? 'service' : 'item';
  // "an item" but "a service" / "a product"
  const scopeArticle = /^[aeiou]/i.test(scopeNoun) ? 'an' : 'a';

  // redeemScope 'selected' restricts the reward to specific catalogue items.
  // The scheme stores commonServiceId for services (and _id for products), so
  // match on the line's commonServiceId first and fall back to serviceId
  // (POS lines already carry commonServiceId in serviceId).
  const redeemItems = (selectedCard?.redeemItems || []).map(String);
  const restrictedToItems = scope === 'selected' && redeemItems.length > 0;
  const lineInScope = (item) => !restrictedToItems
    || redeemItems.includes(String(item.commonServiceId))
    || redeemItems.includes(String(item.serviceId));

  // Eligible checkout lines for this reward's scope. Notional = the line's
  // per-unit effective price (after any per-item discount).
  const eligibleLines = (state.lineItems || [])
    .map((item, index) => ({
      key: String(index),
      type: item.type,
      name: item.name || (item.type === 'product' ? 'Product' : 'Service'),
      price: calcItemEffectivePrice(item),
      inScope: lineInScope(item),
    }))
    .filter((l) => l.price > 0 && l.inScope && (
      scope === 'services' ? l.type === 'service'
        : scope === 'products' ? l.type === 'product'
        : (l.type === 'service' || l.type === 'product')
    ));

  // With a single eligible line there is nothing to choose, so pick it for them.
  const onlyLine = eligibleLines.length === 1 ? eligibleLines[0] : null;
  const effectiveLineKey = onlyLine ? onlyLine.key : lineKey;
  const selectedLine = eligibleLines.find((l) => l.key === effectiveLineKey) || null;

  // Reward value cap: a 'cap' reward comps at most `rewardCap` — the client pays
  // any excess on a dearer line and loses any shortfall on a cheaper one. 'any'
  // covers the chosen line in full. The comp (notional) is what the type-16 row
  // carries; the till nets it off the balance, leaving `clientPays` to collect.
  const capped = selectedCard && selectedCard.rewardValueMode !== 'any'
    && selectedCard.rewardCap != null && selectedCard.rewardCap > 0;
  const cap = capped ? selectedCard.rewardCap : Infinity;
  const linePrice = selectedLine ? selectedLine.price : 0;
  const notional = Math.round(Math.min(linePrice, cap) * 100) / 100;
  const clientPays = Math.round((linePrice - notional) * 100) / 100;   // excess on a dearer line
  const wasted = capped && selectedLine ? Math.round(Math.max(0, cap - linePrice) * 100) / 100 : 0; // lost on a cheaper line

  const rowStyle = (active, selectable = true) => ({
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 14px', marginBottom: 8, borderRadius: 10,
    cursor: selectable ? 'pointer' : 'default',
    border: `1.5px solid ${active ? '#007EA0' : '#e5e5ea'}`,
    background: active ? 'rgba(0,126,160,0.06)' : '#fff',
  });

  const handleApply = () => {
    if (!selectedCard) { setError('Select a reward'); return; }
    if (!selectedLine) { setError(`Select ${scopeArticle} ${scopeNoun} to apply the reward to`); return; }
    if (notional <= 0) { setError('This reward has no value to apply here'); return; }
    if (!tnc) { setError("Confirm the reward is being redeemed in line with the scheme's terms"); return; }
    // One comp per scheme per checkout.
    const already = (state.transactions || []).some(
      (t) => Number(t.paymentType) === PAYMENT_TYPES.LOYALTY_REWARD && String(t.loyaltySchemeId) === String(selectedCard.schemeId)
    );
    if (already) {
      toast.error('That reward is already applied to this checkout');
      return;
    }
    actions.addTransaction({
      paymentType: PAYMENT_TYPES.LOYALTY_REWARD,
      amount: notional,
      label: `Loyalty reward: ${selectedCard.rewardLabel || 'Free ' + scopeNoun}`,
      loyaltySchemeId: selectedCard.schemeId,
    });
    // addTransaction returns to the summary automatically.
  };

  return (
    <div className="co-loyalty">
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="co-screen-title">Redeem Loyalty Reward</h2>
      </div>

      {cards.length === 0 ? (
        <div className="co-section">
          <p className="co-voucher-hint">This client has no rewards to redeem.</p>
        </div>
      ) : (
        <div className="co-section">
          {/* Reward picker */}
          <div className="co-section-title">Reward</div>
          {cards.map((c) => {
            const active = String(schemeId) === String(c.schemeId);
            return (
              <div
                key={String(c.schemeId)}
                style={rowStyle(active)}
                onClick={() => { setSchemeId(String(c.schemeId)); setLineKey(''); setError(''); }}
              >
                {/* The designer collects the reward's name as rewardLabel in
                    stamps mode and pointsLabel in points mode, so read both —
                    on rewardLabel alone every points reward reads "Free reward"
                    and staff can't tell two schemes apart in the picker. */}
                <span style={{ fontWeight: 600 }}>{c.rewardLabel || c.pointsLabel || 'Free reward'}</span>
                <span style={{ color: '#8e8e93', fontSize: 13 }}>{c.schemeName || ''}</span>
              </div>
            );
          })}

          {/* Eligible line. With only one there is nothing to choose, so it is
              auto-selected and shown read-only rather than as a picker. */}
          <div className="co-section-title" style={{ marginTop: 20 }}>
            {onlyLine ? 'Applies to' : `Apply to ${scopeArticle} ${scopeNoun}`}
          </div>
          {!selectedCard ? (
            <p className="co-voucher-hint">Pick a reward first.</p>
          ) : eligibleLines.length === 0 ? (
            <p className="co-error-msg">
              {restrictedToItems
                ? `This reward only covers selected ${scopeNoun}s, and none of them are on this checkout.`
                : `No eligible ${scopeNoun}s on this checkout for this reward.`}
            </p>
          ) : (
            eligibleLines.map((l) => {
              const active = effectiveLineKey === l.key;
              return (
                <div
                  key={l.key}
                  style={rowStyle(active, !onlyLine)}
                  onClick={onlyLine ? undefined : () => { setLineKey(l.key); setError(''); }}
                >
                  <span>{l.name}</span>
                  <span style={{ fontWeight: 600 }}>{fmt(l.price)}</span>
                </div>
              );
            })
          )}

          {selectedLine && (
            <p className="co-voucher-hint" style={{ marginTop: 12 }}>
              {clientPays > 0 ? (
                <>Reward covers <strong>{fmt(notional)}</strong> of <strong>{selectedLine.name}</strong> ({fmt(selectedLine.price)}), so the client pays the <strong>{fmt(clientPays)}</strong> difference.</>
              ) : wasted > 0 ? (
                <>This reward is worth up to {fmt(cap)} and covers <strong>{selectedLine.name}</strong> ({fmt(selectedLine.price)}) in full. The {fmt(wasted)} difference is lost, with no cash back.</>
              ) : (
                <>This reward covers <strong>{selectedLine.name}</strong> ({fmt(selectedLine.price)}) in full. No cash back or change is given for any difference.</>
              )}
            </p>
          )}

          {/* T&C confirmation */}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 16, cursor: 'pointer' }}>
            <input type="checkbox" checked={tnc} onChange={(e) => { setTnc(e.target.checked); setError(''); }} style={{ marginTop: 3 }} />
            <span style={{ fontSize: 14 }}>I confirm this reward is being redeemed in line with the scheme's terms.</span>
          </label>

          {error && <p className="co-error-msg">{error}</p>}

          <button
            className="co-btn co-btn--primary"
            style={{ width: '100%', marginTop: 20 }}
            onClick={handleApply}
            disabled={!selectedCard || !selectedLine || !tnc || notional <= 0}
          >
            {selectedLine ? `Apply reward (${fmt(notional)})` : 'Apply reward'}
          </button>
        </div>
      )}
    </div>
  );
};

export default LoyaltyScreen;
